import { assessContext, overallIdentity } from "../../../utils/identityContext";
import IdentityContext, { ContextLimitation } from "../common/IdentityContext";
import { sanctionsNameStrength, sanctionsMatchReason } from "../../../utils/sanctionsNameMatch";
import { isSubjectReviewComplete } from "../../../utils/exposureReview";
import React, { useMemo, useState } from "react";
import "../css/KycSectionPepSanctionWithScreening.css";
import { getSanctionsSupportingInformation, getSanctionsReviewState, sanctionsSubjectKey } from "../../../utils/sanctionsReview";
import { Block } from "./ViewUI";

import Lottie from "lottie-react";
import pepAnim from "../../../images/sanctions-lottie.json";
import overallAlertAnim from "../../../images/alert-lottie.json";
import overallClearedAnim from "../../../images/cleared-lottie.json";
import overallPendingAnim from "../../../images/pending-lottie.json";

// -------- helpers (same style as your editable section) --------
function safeStr(v) {
  return String(v == null ? "" : v);
}
function clean(v) {
  return safeStr(v)
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^"(.*)"$/, "$1");
}
function normKey(v) {
  return clean(v).toLowerCase();
}
function badge(text, scheme = "grey") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : scheme === "orange"
      ? "badge badgeStandard badgeKycOrange"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}
function defaultContextAssessment() {
  return {
    code: "no_context",
    label: "No context",
    checks: {
      nationality: "not_comparable",
      domicile: "not_comparable",
      age: "not_comparable",
    },
  };
}
function contextBadge(context) {
  const code = clean(context?.code);
  const label =
    code === "context_supports"
      ? "Likely same person"
      : code === "context_mixed"
      ? "Possibly same person"
      : code === "context_conflicts"
      ? "Likely false positive"
      : "Context unclear";
  if (code === "context_supports") return badge(label, "danger");
  if (code === "context_mixed") return badge(label, "warning");
  if (code === "context_conflicts") return badge(label, "success");
  return badge(label, "grey");
}
function overallMatchAssessment(strength, context) { return overallIdentity(strength, context); }

function overallMatchBadge(strength, context) {
  const overall = overallMatchAssessment(strength, context);
  return badge(overall.label, overall.scheme);
}

function contextCheckChipStyles(value) {
  return {
    background: "#EEEAE9",
    borderColor: "#DED8D6",
    color: "#3F4652",
  };
}


function pluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

// ✅ build uniqueMatches exactly like your editable sanctions section  [oai_citation:2‡KycFormPage.css](sediment://file_00000000bb3c720aa31678e182b98b60)
function buildUniqueSanctionsMatches(screeningResult) {
  const matches = Array.isArray(screeningResult?.matches)
    ? screeningResult.matches
    : [];
  if (!matches.length) return [];

  const map = new Map();

  for (const m of matches) {
    const name =
      clean(m?.record?.name) ||
      clean(m?.matchedName) ||
      clean(m?.name) ||
      clean(m?.candidateName) ||
      clean(m?.record?.name) ||
      "Unknown";

    const src =
      clean(
        m?.source || m?.key || m?.list || m?.record?.source || ""
      ).toUpperCase() || "LIST";

    const uid =
      clean(m?.record?.uid) ||
      clean(m?.record?.DATAID) ||
      clean(m?.record?.dataid) ||
      clean(m?.record?.id) ||
      clean(m?.uid) ||
      clean(m?.id) ||
      "";

    const subjectName = clean(m?.__subjectName || "");
    const subjectKey = subjectName ? normKey(subjectName) : "unknown_subject";

    const key = m?.listingKey ? `${m.listingKey}:${subjectKey}` : uid
      ? `${src}:${uid}:${subjectKey}`
      : `${src}:${name.toLowerCase()}:${subjectKey}`;

    const score = Number(m?.score);
    const strength = sanctionsNameStrength(m);

    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        _key: key,
        name,
        source: src,
        score: Number.isFinite(score) ? score : null,
        strength,
        matchType: clean(m?.matchType || m?.reason || m?.type || ""),
        record: m?.record || m,
        subjectName,
        roles: Array.isArray(m?.__roles) ? m.__roles : [],
        contextAssessment: m?.contextAssessment || defaultContextAssessment(),
        _raw: m,
      });
    } else {
      const prevScore = Number(prev.score);
      const nextScore = Number(score);
      const bestRaw = !Number.isFinite(prevScore)
        ? m
        : Number.isFinite(nextScore) && nextScore > prevScore
        ? m
        : prev._raw;

      const bestScore = Number(bestRaw?.score);
      prev._raw = bestRaw;
      prev.score = Number.isFinite(bestScore) ? bestScore : prev.score;
      prev.strength = sanctionsNameStrength(bestRaw);

      const mt = clean(
        bestRaw?.matchType || bestRaw?.reason || bestRaw?.type || ""
      );
      if (mt) prev.matchType = mt;

      const rec = bestRaw?.record || bestRaw;
      if (rec) prev.record = rec;
      prev.contextAssessment =
        bestRaw?.contextAssessment ||
        prev.contextAssessment ||
        defaultContextAssessment();
    }
  }

  return Array.from(map.values());
}

// same renderDetails idea (simplified) from your editable sanctions
function renderDetails(rec) {
  const r = rec || {};
  const lines = [];

  const designation = Array.isArray(r?.designation)
    ? r.designation.filter(Boolean).join(", ")
    : clean(r?.designation);
  const nationality = Array.isArray(r?.nationality)
    ? r.nationality.filter(Boolean).join(", ")
    : clean(r?.nationality);
  const dob = Array.isArray(r?.dob)
    ? r.dob.filter(Boolean).join(", ")
    : clean(r?.dob);
  const pob = Array.isArray(r?.pob)
    ? r.pob.filter(Boolean).join(" · ")
    : clean(r?.pob);
  const addr = Array.isArray(r?.addresses)
    ? r.addresses.filter(Boolean).join(" · ")
    : clean(r?.address);
  const notes = clean(
    r?.remarks || r?.comments || r?.COMMENTS1 || r?.comments1
  );

  if (designation) lines.push({ k: "Designation", v: designation });
  if (nationality) lines.push({ k: "Nationality", v: nationality });
  if (dob) lines.push({ k: "Date of birth", v: dob });
  if (pob) lines.push({ k: "Place of birth", v: pob });
  if (addr) lines.push({ k: "Address", v: addr });
  if (notes) lines.push({ k: "Notes", v: notes });

  if (!lines.length)
    return (
      <div className="small text-muted">No additional details available.</div>
    );

  return (
    <div className="small">
      {lines.map((x, i) => (
        <div key={i} className="mb-1">
          <span className="text-muted">{x.k}:</span>{" "}
          <span style={{ color: "#2f3441" }}>{x.v}</span>
        </div>
      ))}
    </div>
  );
}

export default function KycViewSanctions({ sectionNo = 0, form }) {
  const sanctions = form?.sanctions || {};
  const screening = form?.internalUse?.sanctionsScreening || null;
  const [showDetails, setShowDetails] = useState(false);

  const screeningResult = screening?.result || null;
  const hasResult = !!screeningResult;

  const subjects = screening?.searchedSubjects || screeningResult?.subjects || [];
  const review = getSanctionsReviewState(form, subjects,
    match => buildUniqueSanctionsMatches({ matches: [match] })[0]?._key);
  const exposureCount = subjects.filter(subject =>
    review.personReviews[sanctionsSubjectKey(subject)]?.decision === "exposure"
  ).length;
  const supportingInformation = getSanctionsSupportingInformation(form);
  const staffDecision = clean(sanctions?.staffDecision) || "";
  const clientDeclared = !!sanctions?.clientDeclared;

  const selectedKeysArr = review.relevantKeys;
  const selectedKeySet = useMemo(
    () => new Set(selectedKeysArr.map(String)),
    [selectedKeysArr]
  );

  const uniqueMatches = useMemo(
    () => buildUniqueSanctionsMatches(screeningResult),
    [screeningResult]
  );

  // Selected supporting evidence; subject outcomes determine exposure.
  const confirmedMatches = useMemo(
    () => uniqueMatches.filter((m) => selectedKeySet.has(m._key)),
    [uniqueMatches, selectedKeySet]
  );

  const reviewedCount = subjects.filter(subject => {
    const key = sanctionsSubjectKey(subject);
    const hasSelectedEvidence = confirmedMatches.some(match =>
      normKey(match.subjectName) === normKey(subject.name) || (!match.subjectName && subjects.length === 1)
    );
    return isSubjectReviewComplete(review.personReviews[key]?.decision, hasSelectedEvidence, sanctions.supportingInformation?.[key]);
  }).length;
  const reviewComplete = subjects.length > 0 && reviewedCount === subjects.length;

  const [openKey, setOpenKey] = useState(null);

  // ---------- Top overview card status logic (same as your view already had) ----------
  const clientStatusBadge = clientDeclared
    ? badge("Declared", "warning")
    : badge("Not declared", "success");
  const clientHint = clientDeclared
    ? "Client reports exposure"
    : "Client reports no exposure";


  const screeningStatusBadge = !hasResult ? badge("Pending", "grey")
    : reviewComplete ? badge(exposureCount ? `${exposureCount} sanctions exposure` : "Reviewed · No exposure", exposureCount ? "danger" : "success")
    : badge("Pending", "grey");
  const screeningHint = !hasResult ? "Screening not started"
    : reviewComplete ? (subjects.length === 1 ? "Subject reviewed" : `All ${subjects.length} subjects reviewed`)
    : `${reviewedCount} of ${subjects.length} ${subjects.length === 1 ? "subject" : "subjects"} reviewed`;
  const complianceUsable = hasResult && reviewComplete;
  const complianceStatusBadge = !complianceUsable ? badge("Pending", "grey")
    : exposureCount === 0 || staffDecision === "flagged_remediated" ? badge("Cleared", "success")
    : staffDecision === "flagged_confirmed" ? badge("Confirmed", "danger")
    : badge("Sanctions exposure identified", "warning");
  const complianceHint = !hasResult ? "Set outcome after screening"
    : !complianceUsable ? "Set outcome after review"
    : exposureCount === 0 ? "No sanctions exposure identified"
    : staffDecision === "flagged_remediated" ? "Exposure flagged · Remediated"
    : staffDecision === "flagged_confirmed" ? "Confirmed sanctions exposure"
    : "Sanctions exposure identified · Pending decision";

  const overall = (() => {
    if (!complianceUsable)
      return {
        term: "Pending",
        bg: "#F4F5F7",
        border: "#D9DCE1",
        icon: "bi-hourglass",
        color: "#6B7C5F",
        lottie: overallPendingAnim,
      };
    if (exposureCount > 0 && staffDecision === "flagged_confirmed")
      return {
        term: "Confirmed",
        bg: "#FDEBEC",
        border: "#F3B6BC",
        icon: "bi-exclamation-circle-fill",
        color: "#C88294",
        lottie: overallAlertAnim,
      };
    if (
      exposureCount === 0 ||
      staffDecision === "flagged_remediated"
    ) {
      return {
        term: "Cleared",
        bg: "#EAF7EE",
        border: "#BFE6CC",
        icon: "bi-check-circle",
        color: "#6B7C5F",
        lottie: overallClearedAnim,
      };
    }
    return {
      term: "Pending",
      bg: "#F4F5F7",
      border: "#FFD79A",
      icon: "bi-hourglass",
      color: "#6B7C5F",
      lottie: overallPendingAnim,
    };
  })();

  // Staff assessment conditional
  const clientCommentsText = String(sanctions?.clientDetails || "").trim();
  const staffAssessmentText = String(sanctions?.staffAssessment || "").trim();
  const hasComplianceAssessment =
    !!clientCommentsText || !!staffAssessmentText;
  const hasExpandableDetails =
    hasComplianceAssessment || confirmedMatches.length > 0 || supportingInformation.length > 0;

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead position-relative">
        <h5 className="kycSectionTitle mb-0">
          {sectionNo ? `${sectionNo} | ` : ""}Sanctions Exposure
        </h5>
      </div>

      <div className="kycSectionBody">
        {/* ✅ TOP SUMMARY CARD (unchanged design) */}
        <div
          className="p-3 border rounded-3 mb-3"
          style={{
            background: "#EBECEF",
            cursor: hasExpandableDetails ? "pointer" : "default",
          }}
          role={hasExpandableDetails ? "button" : undefined}
          tabIndex={hasExpandableDetails ? 0 : undefined}
          onClick={() => {
            if (hasExpandableDetails) setShowDetails((v) => !v);
          }}
          onKeyDown={(e) => {
            if (!hasExpandableDetails) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setShowDetails((v) => !v);
            }
          }}
        >
          <div
            className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
            style={{ height: 30, marginBottom: 5, marginTop: 0 }}
          >
            <div>
              <div
                className="fw-semibold d-flex align-items-center gap-2"
                style={{ color: "#2f3441" }}
              >
                {/* <i
                  className="bi bi-shield-lock me-2"
                  style={{ color: "#A1A5AE", fontSize: 25 }}
                /> */}
                <span
                  style={{
                    width: 40,
                    height: 40,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                  }}
                >
                  <Lottie
                    animationData={pepAnim}
                    loop
                    autoplay
                    style={{ width: 40, height: 40 }}
                  />
                </span>
                Sanctions overview
              </div>
            </div>

            {hasExpandableDetails ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetails((v) => !v);
                }}
                style={{ height: 30 }}
              >
                {showDetails ? (
                  <>
                    <i className="bi bi-chevron-up me-1" />
                    Hide details
                  </>
                ) : (
                  <>
                    <i className="bi bi-chevron-down me-1" />
                    Show details
                  </>
                )}
              </button>
            ) : null}
          </div>

          <hr className="my-3" />

          <div className="row g-2">
            <div className="col-12 col-md">
              <div className="p-2 rounded-3 border bg-white h-100">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    {/* <i className="bi bi-person-check" /> */}
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 22,
                        height: 22,
                        color: "#817672",
                        flex: "0 0 auto",
                      }}
                      aria-hidden="true"
                    >
                      <svg
                        id="fi_8703172"
                        height="512"
                        viewBox="0 0 60 60"
                        width="512"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="currentColor"
                          d="m34.82 8.06c-5.71 0-10.35 4.64-10.35 10.35s4.64 10.35 10.35 10.35 10.35-4.64 10.35-10.35-4.65-10.35-10.35-10.35zm0 18.69c-4.6 0-8.35-3.74-8.35-8.35s3.74-8.35 8.35-8.35c4.6 0 8.35 3.74 8.35 8.35s-3.75 8.35-8.35 8.35z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="m22.54 38.64c.66.61 1.04 1.49 1.04 2.41v9.3c0 .55.45 1 1 1s1-.45 1-1v-9.3c0-1.47-.61-2.88-1.68-3.88-5.42-5.04-10.5-12.12-15.54-21.67-.73-1.39-.32-3.14.95-4.07.75-.54 1.7-.73 2.6-.52s1.66.8 2.09 1.61c3.05 5.79 6.49 11.08 10.21 15.73 1.83 2.28 4.56 3.58 7.49 3.58h9.16c6.14 0 11.14 5 11.14 11.14v7.37c0 .55.45 1 1 1s1-.45 1-1v-7.37c0-7.25-5.89-13.14-13.14-13.14h-9.16c-2.33 0-4.49-1.03-5.93-2.83-3.64-4.54-7.01-9.73-10-15.41-.7-1.33-1.94-2.29-3.4-2.63s-3-.03-4.23.85c-2.08 1.51-2.74 4.35-1.55 6.62 5.15 9.76 10.36 17.02 15.95 22.21z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="m46 43.71v7.23c0 .55.45 1 1 1s1-.45 1-1v-7.23c0-.55-.45-1-1-1s-1 .44-1 1z"
                        ></path>
                      </svg>
                    </span>
                    <div className="fw-semibold small">Client declaration</div>
                  </div>
                  {clientStatusBadge}
                </div>
                <div className="small text-muted mt-1">{clientHint}</div>
              </div>
            </div>

            <div className="col-12 col-md">
              <div className="p-2 rounded-3 border bg-white h-100">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    {/* <i className="bi bi-search" /> */}
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 22,
                        height: 22,
                        color: "#817672",
                        flex: "0 0 auto",
                      }}
                      aria-hidden="true"
                    >
                      <svg
                        height="512"
                        viewBox="0 0 64 64"
                        width="512"
                        xmlns="http://www.w3.org/2000/svg"
                        id="fi_6772342"
                      >
                        <g id="_10-Security_Gate" data-name="10-Security Gate">
                          <path
                            fill="currentColor"
                            d="m53 2h-42a.99974.99974 0 0 0 -1 1v58a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-43h32v43a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-58a.99974.99974 0 0 0 -1-1zm-39 58h-2v-56h2zm34-44h-32v-12h32zm4 44h-2v-56h2zm-26-46h12a.99974.99974 0 0 0 1-1v-6a.99974.99974 0 0 0 -1-1h-12a.99974.99974 0 0 0 -1 1v6a.99974.99974 0 0 0 1 1zm1-6h10v4h-10zm-9 0a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm23-4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm-4 20h-.54047a6 6 0 1 0 -8.91906 0h-.54047a7.00818 7.00818 0 0 0 -7 7v6a3.00328 3.00328 0 0 0 3 3h2v11a3.00328 3.00328 0 0 0 3 3h8a3.00328 3.00328 0 0 0 3-3v-11h2a3.00328 3.00328 0 0 0 3-3v-6a7.00818 7.00818 0 0 0 -7-7zm-5-8a4 4 0 1 1 -4 4 4.00427 4.00427 0 0 1 4-4zm10 21a1.001 1.001 0 0 1 -1 1h-2v-5a1 1 0 0 0 -2 0v18a1.001 1.001 0 0 1 -1 1h-3v-9a1 1 0 0 0 -2 0v9h-3a1.001 1.001 0 0 1 -1-1v-18a1 1 0 0 0 -2 0v5h-2a1.001 1.001 0 0 1 -1-1v-6a5.00588 5.00588 0 0 1 5-5h10a5.00588 5.00588 0 0 1 5 5zm-34-13a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-4.55469-9.168a1 1 0 1 1 1.10938-1.664l3 2a1 1 0 1 1 -1.10938 1.664zm4.38669 16.61331a.9993.9993 0 0 1 -.27731 1.38669l-3 2a1 1 0 0 1 -1.10938-1.664l3-2a.99886.99886 0 0 1 1.38669.27731zm54.168-7.44531a.99974.99974 0 0 1 -1 1h-4a1 1 0 0 1 0-2h4a.99974.99974 0 0 1 1 1zm-5.832-7.44531a.9993.9993 0 0 1 .27734-1.38672l3-2a1 1 0 1 1 1.10935 1.66403l-3 2a.99992.99992 0 0 1 -1.38672-.27734zm4.38669 16.61331a1 1 0 1 1 -1.10938 1.664l-3-2a1 1 0 0 1 1.10938-1.664z"
                          ></path>
                        </g>
                      </svg>
                    </span>
                    <div className="fw-semibold small">Screening</div>
                  </div>
                  {screeningStatusBadge}
                </div>
                <div className="small text-muted mt-1">{screeningHint}</div>
              </div>
            </div>

            <div className="col-12 col-md">
              <div className="p-2 rounded-3 border bg-white h-100">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    {/* <i className="bi bi-clipboard-check" /> */}
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 22,
                        height: 22,
                        color: "#817672",
                        flex: "0 0 auto",
                      }}
                      aria-hidden="true"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        id="fi_14979987"
                        viewBox="0 0 60 60"
                      >
                        <path
                          fill="currentColor"
                          d="M36.949,31.846A3.005,3.005,0,0,0,39,29V26a9,9,0,0,0-18,0v3a3.005,3.005,0,0,0,2.051,2.846A7,7,0,0,0,26,36.734v1.5l-3.9,1.053a7.242,7.242,0,0,0-5.228,4.542L15.6,47.4l-2.4-1.917a1.579,1.579,0,0,0-.2-.13V42.68a2.685,2.685,0,0,0-5.29-.642l-.007.026a4.629,4.629,0,0,0-4.211,1.453,1.892,1.892,0,0,0,.617,2.995,19.054,19.054,0,0,1,3.045,1.7c.465.336.843.633,1.214.935a1.83,1.83,0,0,0,.651,1.2l6.43,4.77a3.45,3.45,0,0,0,3.086.8,3.406,3.406,0,0,0,2.415-1.958L22,51.992V58a2,2,0,0,0,2,2H36a2,2,0,0,0,2-2V51.987l1.013,1.892a3.456,3.456,0,0,0,2.452,2.036,3.4,3.4,0,0,0,3.016-.744l6.568-4.882a1.769,1.769,0,0,0,.583-1.14c.371-.3.747-.6,1.207-.933a19.142,19.142,0,0,1,3.055-1.706,1.891,1.891,0,0,0,.6-3,4.641,4.641,0,0,0-4.2-1.442l-.007-.029A2.685,2.685,0,0,0,47,42.68v2.668a1.718,1.718,0,0,0-.2.131l-2.326,1.937-1.345-3.588a7.219,7.219,0,0,0-5.184-4.53L34,38.234v-1.5A7,7,0,0,0,36.949,31.846ZM30,19a7.009,7.009,0,0,1,7,7v.179c-.037-.013-.072-.032-.11-.044a3.073,3.073,0,0,0-4.626-1.743,4.521,4.521,0,0,1-4.527,0,3.075,3.075,0,0,0-4.629,1.743c-.037.011-.071.03-.108.043V26A7.009,7.009,0,0,1,30,19ZM24,30a1,1,0,0,1,0-2,1.081,1.081,0,0,0,1-1.075,1.031,1.031,0,0,1,.564-.9,1.1,1.1,0,0,1,.51-.129,1.023,1.023,0,0,1,.564.169,6.5,6.5,0,0,0,6.723,0,1.041,1.041,0,0,1,1.073-.04A1.064,1.064,0,0,1,35,27a1,1,0,0,0,1,1,1,1,0,0,1,0,2,1,1,0,0,0-1,1,5,5,0,0,1-10,0A1,1,0,0,0,24,30Zm9.439,10.155,1.921.517a6.768,6.768,0,0,1-2.036,2.614.392.392,0,0,1-.365.049.38.38,0,0,1-.251-.261,5.694,5.694,0,0,0-.793-1.654A4.831,4.831,0,0,0,33.439,40.155ZM31.984,52.646,30,53.965l-1.984-1.319L28.862,47h2.276ZM28.721,45l-.116-.348a2.294,2.294,0,0,0,.6-1.006A3.239,3.239,0,0,1,30,42.215a3.2,3.2,0,0,1,.788,1.417,2.323,2.323,0,0,0,.608,1.019L31.279,45Zm-1.433-1.911a.373.373,0,0,1-.247.246.4.4,0,0,1-.37-.052,6.8,6.8,0,0,1-2.034-2.609l1.924-.519a4.851,4.851,0,0,0,1.525,1.266A5.738,5.738,0,0,0,27.288,43.089ZM5.075,44.754a2.634,2.634,0,0,1,2.935-.522,1,1,0,0,0,1.38-.669l.259-1.037A.7.7,0,0,1,10.32,42a.681.681,0,0,1,.68.68v2.769a1.8,1.8,0,0,0-.233.205l-1.524,1.63c-.28-.221-.581-.451-.924-.7A20.843,20.843,0,0,0,5.075,44.754ZM49.68,42a.7.7,0,0,1,.671.522l.259,1.041a1,1,0,0,0,1.38.669,2.69,2.69,0,0,1,2.962.509,20.887,20.887,0,0,0-3.278,1.849c-.339.245-.637.473-.916.693l-1.525-1.629A1.8,1.8,0,0,0,49,45.449V42.68A.681.681,0,0,1,49.68,42Zm-8.424,2.53L42.6,48.118a1.956,1.956,0,0,0,3.052.84l2.248-1.8,1.647,1.759-6.327,4.7a1.478,1.478,0,0,1-1.321.342,1.542,1.542,0,0,1-1.087-.949l-2.931-5.486A1,1,0,0,0,36,48V58H24V48a1,1,0,0,0-1.882-.472l-2.969,5.564a1.481,1.481,0,0,1-1.049.871,1.532,1.532,0,0,1-1.389-.4L10.45,48.921,12.1,47.159l2.248,1.8a1.955,1.955,0,0,0,3.052-.84l1.345-3.586a5.271,5.271,0,0,1,3.832-3.3l.1-.027a9.027,9.027,0,0,0,2.805,3.692,2.356,2.356,0,0,0,1.241.443l.25.75-.956,6.388a1.828,1.828,0,0,0,.794,1.774L28.989,55.7a1.823,1.823,0,0,0,2.022,0l2.176-1.446a1.831,1.831,0,0,0,.793-1.781l-.955-6.381.247-.742a2.359,2.359,0,0,0,1.239-.449,8.969,8.969,0,0,0,2.806-3.7l.151.04A5.247,5.247,0,0,1,41.256,44.53ZM32,38.743a2.944,2.944,0,0,1-2,1.243,2.946,2.946,0,0,1-2-1.241V37.706a6.95,6.95,0,0,0,4,0Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M11,22A11,11,0,1,0,0,11,11.013,11.013,0,0,0,11,22ZM11,2a9,9,0,1,1-9,9A9.01,9.01,0,0,1,11,2Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M49,0A11,11,0,1,0,60,11,11.013,11.013,0,0,0,49,0Zm0,20a9,9,0,1,1,9-9A9.01,9.01,0,0,1,49,20Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M7.293,14.707a1,1,0,0,0,1.414,0L11,12.414l2.293,2.293a1,1,0,0,0,1.414-1.414L12.414,11l2.293-2.293a1,1,0,1,0-1.414-1.414L11,9.586,8.707,7.293A1,1,0,1,0,7.293,8.707L9.586,11,7.293,13.293A1,1,0,0,0,7.293,14.707Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M51.168,7.445l-3.323,4.986-1.138-1.138a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,48,15c.032,0,.065,0,.1-.005a1,1,0,0,0,.733-.44l4-6a1,1,0,0,0-1.664-1.11Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M50,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                        ></path>
                        <path
                          fill="currentColor"
                          d="M12,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                        ></path>
                      </svg>
                    </span>
                    <div className="fw-semibold small">Assessment</div>
                  </div>
                  {complianceStatusBadge}
                </div>
                <div className="small text-muted mt-1">{complianceHint}</div>
              </div>
            </div>

            <div className="col-12 col-md-2">
              <div
                className="p-2 rounded-3 border h-100 d-flex align-items-center justify-content-center text-center"
                style={{ background: overall.bg, borderColor: overall.border }}
              >
                <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                  <span
                    style={{
                      width: 35,
                      height: 35,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 auto",
                      lineHeight: 1,
                    }}
                    aria-hidden="true"
                  >
                    <Lottie
                      animationData={overall.lottie}
                      loop
                      autoplay
                      style={{ width: 35, height: 35 }}
                    />
                  </span>
                  {/* <i
                    className={`bi ${overall.icon}`}
                    style={{
                      fontSize: 24,
                      lineHeight: 1,
                      color: overall.color,
                    }}
                    aria-hidden="true"
                  /> */}
                  <div
                    className="fw-semibold small"
                    style={{ color: overall.color, fontSize: 12 }}
                  >
                    {overall.term}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showDetails ? (
          <>
            {hasComplianceAssessment ? (
              <div className="row g-3 mt-1">
                <div className="col-12">
                  <div
                    className="rounded-3 p-3"
                    style={{
                      background: "#F9F7F5",
                      border: "1px solid #DBD6D2",
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "Roboto, Arial, sans-serif",
                        fontSize: 18,
                        fontWeight: 500,
                        color: "#8B807B",
                        marginBottom: 14,
                      }}
                    >
                      Compliance assessment
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <Block
                          label="Client comments"
                          value={clientCommentsText || "—"}
                        />
                      </div>
                      <div className="col-12 col-md-8">
                        <Block
                          label="Assessment comments"
                          value={staffAssessmentText || "—"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {supportingInformation.map(({ key, name, text }) => (
              <div key={key} className="rounded-3 border p-3 mt-3" style={{ background: "#F9F7F5" }}>
                <div style={{ fontFamily: '"Nunito", sans-serif', fontWeight: 500, fontSize: 13 }}>Other supporting information</div>
                <div className="small text-muted mt-2">{name}</div>
                <div className="small mt-1" style={{ whiteSpace: "pre-wrap" }}>{text}</div>
              </div>
            ))}
            {confirmedMatches.length ? (
              <div className="mt-3">
                <div
                  className="rounded-3 p-3"
                  style={{
                    background: "#F9F7F5",
                    border: "1px solid #DBD6D2",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Roboto, Arial, sans-serif",
                      fontSize: 18,
                      fontWeight: 500,
                      color: "#8B807B",
                      marginBottom: 14,
                    }}
                  >
                    Sanctions screening
                  </div>

                  <div style={{ maxHeight: 420, overflowY: "auto" }}>
                    <div className="list-group">
                      {confirmedMatches.map((m) => {
                        const expanded = openKey === m._key;
                        const context =
                          assessContext(m._raw?.__subjectMeta || {}, m.record, "sanctions");

                        const src = clean(m.source).toUpperCase();
                        const matchLabel = sanctionsMatchReason(m._raw || m, m.name);
                        const subjectName = clean(m.subjectName);

                        const previewParts = [];
                        if (src) previewParts.push(`Source: ${src}`);
                        if (subjectName)
                          previewParts.push(`Screened: ${subjectName}`);
                        if (matchLabel) previewParts.push(`Match: ${matchLabel}`);

                        return (
                          <div
                            key={m._key}
                            className="list-group-item"
                            style={{
                              borderLeft: "4px solid #EBE9E8",
                              background: "#FCFBFA",
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-between gap-3"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setOpenKey(expanded ? null : m._key)
                              }
                              role="button"
                            >
                              <div style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <div
                                    className="fw-semibold"
                                    style={{ color: "#2f3441" }}
                                  >
                                    {m.name}
                                  </div>
                                  {overallMatchBadge(m.strength, context)}
                                  <ContextLimitation context={context} />
                                  {badge("Selected", "grey")}
                                </div>

                                {previewParts.length ? (
                                  <div
                                    className="small text-muted mt-1 kycClamp3"
                                    style={{ opacity: 0.9 }}
                                  >
                                    {previewParts.join(" · ")}
                                  </div>
                                ) : null}
                              </div>

                              <div
                                className="text-muted small"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {expanded ? "Hide" : "Details"}{" "}
                                <i
                                  className={`bi ${
                                    expanded ? "bi-chevron-up" : "bi-chevron-down"
                                  }`}
                                />
                              </div>
                            </div>

                            {expanded ? (
                              <div className="mt-3">
                                <IdentityContext context={context} strength={m.strength} nameReason={matchLabel}
                                  screenedName={m.subjectName} comparedName={m.name} />
                                <div
                                  className="p-2 rounded-3 border"
                                  style={{
                                    backgroundColor: "#F2F0EF",
                                    border: "none",
                                    color: "#837E7B",
                                  }}
                                >
                                  {renderDetails(m.record)}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
